import { useMemo, useState } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import SEOHead from '../../components/seo/SEOHead';

type FormData = {
  name: string;
  email: string;
  businessName: string;
  website: string;
  industry: string;
  goals: string;
  audience: string;
  style: string;
  colors: string;
  mustHave: string;
  examples: string;
  timeline: string;
  budget: string;
  notes: string;
  calendlyBooked: string;
  calendlyEventUrl: string;
};

const steps = ['Basics', 'Brand + Goals', 'Scope', 'Calendly + Submit'];

export default function VibeCheckPage() {
  const siteUrl = import.meta.env.VITE_SITE_URL || 'https://neurasites.ai';
  const calendlyUrl = import.meta.env.VITE_CALENDLY_URL || 'https://calendly.com/khtims75/30min';
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState<'idle' | 'saving' | 'ok' | 'error'>('idle');
  const [statusMsg, setStatusMsg] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [form, setForm] = useState<FormData>({
    name: '',
    email: '',
    businessName: '',
    website: '',
    industry: '',
    goals: '',
    audience: '',
    style: '',
    colors: '',
    mustHave: '',
    examples: '',
    timeline: '',
    budget: '',
    notes: '',
    calendlyBooked: 'no',
    calendlyEventUrl: ''
  });

  const progress = useMemo(() => Math.round((step / steps.length) * 100), [step]);

  const focusFirstError = (nextErrors: Record<string, string>) => {
    const firstField = Object.keys(nextErrors)[0];
    if (!firstField) return;
    const field = document.querySelector(`[name="${firstField}"]`) as HTMLElement | null;
    if (!field) return;
    field.scrollIntoView({ behavior: 'smooth', block: 'center' });
    (field as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement).focus();
  };

  const validateStep = (stepToValidate: number) => {
    const nextErrors: Record<string, string> = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (stepToValidate === 1) {
      if (!form.name.trim()) nextErrors.name = 'Name is required.';
      if (!form.email.trim()) nextErrors.email = 'Email is required.';
      else if (!emailRegex.test(form.email.trim())) nextErrors.email = 'Enter a valid email address.';
      if (!form.businessName.trim()) nextErrors.businessName = 'Business name is required.';
    }

    if (stepToValidate === 2) {
      if (!form.goals.trim()) nextErrors.goals = 'Please share your top goals.';
      if (!form.audience.trim()) nextErrors.audience = 'Please describe your ideal customer.';
    }

    if (stepToValidate === 3) {
      if (!form.mustHave.trim()) nextErrors.mustHave = 'Please list must-have pages/features.';
      if (!form.timeline.trim()) nextErrors.timeline = 'Please share your desired timeline.';
      if (!form.budget.trim()) nextErrors.budget = 'Please share a budget range.';
    }

    if (stepToValidate === 4 && form.calendlyBooked === 'yes' && !form.calendlyEventUrl.trim()) {
      nextErrors.calendlyEventUrl = 'Add your Calendly event link or switch booking status to not booked yet.';
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      focusFirstError(nextErrors);
      return false;
    }
    return true;
  };

  const onChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => {
      if (!prev[name]) return prev;
      const copy = { ...prev };
      delete copy[name];
      return copy;
    });
  };

  const next = () => {
    if (!validateStep(step)) return;
    setStep((s) => Math.min(steps.length, s + 1));
  };

  const prev = () => {
    setErrors({});
    setStep((s) => Math.max(1, s - 1));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(4)) return;

    setStatus('saving');
    setStatusMsg('Sending your vibe check...');
    try {
      const res = await fetch('/api/vibe-check.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok || !data?.ok) throw new Error(data?.error || 'submit_failed');
      setStatus('ok');
      setStatusMsg('Sent. We received your vibe check and will follow up soon.');
    } catch (err) {
      setStatus('error');
      setStatusMsg('Could not send the form. Please try again or email hello@neurasites.ai.');
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900 transition-colors">
      <SEOHead
        title="Vibe Check | NeuraSites AI"
        description="Complete the guided Vibe Check to help us build your new website faster and more accurately."
        canonicalUrl={`${siteUrl}/vibe-check`}
      />

      <Header />

      <main>
        <section className="relative overflow-hidden brand-soft-surface py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h1 className="text-5xl md:text-6xl font-bold text-gray-900 dark:text-white mb-6">
              5‑Minute
              <span className="block brand-heading-accent">
                Vibe Check
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300">
              Quick guided intake so your project lands right the first time.
            </p>
          </div>
        </section>

        <section className="py-12 bg-white dark:bg-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="mb-6">
              <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
                <span>Step {step} of {steps.length}</span>
                <span>{progress}%</span>
              </div>
              <div className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                <div className="h-2 bg-blue-600" style={{ width: `${progress}%` }} />
              </div>
              <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">{steps[step - 1]}</p>
            </div>

            <form onSubmit={submit} className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg p-6 md:p-8 space-y-6">
              {step === 1 && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <input name="name" value={form.name} onChange={onChange} placeholder="Your name *" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                      {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
                    </div>
                    <div>
                      <input name="email" value={form.email} onChange={onChange} type="email" placeholder="Email *" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                      {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
                    </div>
                    <div className="md:col-span-2">
                      <input name="businessName" value={form.businessName} onChange={onChange} placeholder="Business name *" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                      {errors.businessName && <p className="mt-1 text-sm text-red-600">{errors.businessName}</p>}
                    </div>
                    <div className="md:col-span-2">
                      <input name="website" value={form.website} onChange={onChange} placeholder="Current website URL" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                    </div>
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <input name="industry" value={form.industry} onChange={onChange} placeholder="Industry / niche" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                  <div>
                    <textarea name="goals" value={form.goals} onChange={onChange} placeholder="Top goals for this site (leads, bookings, sales, authority, etc.)" rows={4} className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                    {errors.goals && <p className="mt-1 text-sm text-red-600">{errors.goals}</p>}
                  </div>
                  <div>
                    <textarea name="audience" value={form.audience} onChange={onChange} placeholder="Who is your ideal customer?" rows={3} className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                    {errors.audience && <p className="mt-1 text-sm text-red-600">{errors.audience}</p>}
                  </div>
                  <textarea name="style" value={form.style} onChange={onChange} placeholder="Preferred style/feel (modern, premium, playful, bold, etc.)" rows={3} className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                  <input name="colors" value={form.colors} onChange={onChange} placeholder="Brand colors / fonts (if any)" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                </>
              )}

              {step === 3 && (
                <>
                  <div>
                    <textarea name="mustHave" value={form.mustHave} onChange={onChange} placeholder="Must-have pages/features (examples: Home, Services, Pricing, Booking, Blog)" rows={4} className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                    {errors.mustHave && <p className="mt-1 text-sm text-red-600">{errors.mustHave}</p>}
                  </div>
                  <textarea name="examples" value={form.examples} onChange={onChange} placeholder="Sites you like (URLs + what you like about them)" rows={4} className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <input name="timeline" value={form.timeline} onChange={onChange} placeholder="Desired timeline" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                      {errors.timeline && <p className="mt-1 text-sm text-red-600">{errors.timeline}</p>}
                    </div>
                    <div>
                      <input name="budget" value={form.budget} onChange={onChange} placeholder="Budget range" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                      {errors.budget && <p className="mt-1 text-sm text-red-600">{errors.budget}</p>}
                    </div>
                  </div>
                  <textarea name="notes" value={form.notes} onChange={onChange} placeholder="Anything else we should know?" rows={4} className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                </>
              )}

              {step === 4 && (
                <>
                  <p className="text-gray-700 dark:text-gray-200">Book your kickoff call before submitting:</p>
                  <a href={calendlyUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">Open Calendly</a>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4">
                    <select name="calendlyBooked" value={form.calendlyBooked} onChange={onChange} className="px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900">
                      <option value="no">Calendly not booked yet</option>
                      <option value="yes">Calendly appointment booked</option>
                    </select>
                    <div>
                      <input name="calendlyEventUrl" value={form.calendlyEventUrl} onChange={onChange} placeholder="Calendly event/link (optional)" className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900" />
                      {errors.calendlyEventUrl && <p className="mt-1 text-sm text-red-600">{errors.calendlyEventUrl}</p>}
                    </div>
                  </div>

                  {status !== 'idle' && (
                    <div className={`rounded-lg p-3 text-sm ${status === 'ok' ? 'bg-green-50 text-green-800' : status === 'error' ? 'bg-red-50 text-red-700' : 'bg-blue-50 text-blue-700'}`}>
                      {statusMsg}
                    </div>
                  )}
                </>
              )}

              <div className="flex items-center justify-between pt-2">
                <button type="button" onClick={prev} disabled={step === 1} className="px-5 py-2 rounded-lg border border-gray-300 dark:border-gray-600 disabled:opacity-50">Back</button>
                {step < steps.length ? (
                  <button type="button" onClick={next} className="px-5 py-2 rounded-lg bg-blue-600 text-white font-semibold">Next</button>
                ) : (
                  <button type="submit" disabled={status === 'saving'} className="px-5 py-2 rounded-lg bg-blue-600 text-white font-semibold disabled:opacity-60">
                    {status === 'saving' ? 'Saving...' : 'Submit Vibe Check'}
                  </button>
                )}
              </div>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
